import React from "react";
import {injectIntl} from "react-intl";
import LidarSensor from "./LidarSensor";
import {isFlexColumn, isAisleMergedOV, shouldUseWideWidth, formatAisleLabel, getDisplayableColumnLabel} from "../../../util/LidarTesterUtil";
import {LIDAR_COLUMN_COLORS, LIDAR_COLUMN_DIMENSIONS} from "../../../util/Constants";
import {WW_FLEX_CART_RECIPES} from "../../../util/Validations";

/**
 * Generates styling for bin columns based on type and merge state
 * 
 * @param {string} columnLabel - Column label for type detection
 * @param {boolean} isAnomaly - Whether column is in anomaly state
 * @param {boolean} shouldMergeRight - Whether to merge with right column
 * @param {boolean} shouldMergeLeft - Whether to merge with left column
 * @param {string} aisleLabel - Aisle label for per-aisle detection
 * @param {Object} device - Device configuration for merge detection
 * @returns {Object} CSS style object
 */
const getBinColumnStyle = (columnLabel, isAnomaly, shouldMergeRight = false, shouldMergeLeft = false, aisleLabel, device) => {
    // Determine background color based on column type and state
    let backgroundColor = LIDAR_COLUMN_COLORS.REGULAR; // Default: white
    
    // Check if this column uses flex cart recipe (for merged flex columns)
    const isFlexCartType = device && aisleLabel && device.displayableAssetMappings && 
        device.displayableAssetMappings.find(mapping => formatAisleLabel(mapping.aisleLabel) === aisleLabel) &&
        device.displayableAssetMappings.find(mapping => formatAisleLabel(mapping.aisleLabel) === aisleLabel).columns &&
        device.displayableAssetMappings.find(mapping => formatAisleLabel(mapping.aisleLabel) === aisleLabel).columns.some(column => {
            const label = getDisplayableColumnLabel(column.columnLabel, column.rowLabels, column.cartType);
            return label === columnLabel && WW_FLEX_CART_RECIPES.includes(column.cartType);
        });
    
    if (isAnomaly) {
        backgroundColor = LIDAR_COLUMN_COLORS.ANOMALY; // Black for errors
    } else if (isFlexColumn(columnLabel) || isFlexCartType) {
        backgroundColor = LIDAR_COLUMN_COLORS.FLEX; // Soft minty green for flex columns
    } else if (isAisleMergedOV(columnLabel, aisleLabel, device)) {
        backgroundColor = LIDAR_COLUMN_COLORS.UNIFIED_OV; // Light blue for dynamically merged OV
    }
    
    return {
        width: shouldUseWideWidth(columnLabel) ? LIDAR_COLUMN_DIMENSIONS.WIDE_WIDTH : LIDAR_COLUMN_DIMENSIONS.NARROW_WIDTH,
        height: LIDAR_COLUMN_DIMENSIONS.HEIGHT,
        display: "flex",
        borderTop: LIDAR_COLUMN_DIMENSIONS.BORDER,
        borderBottom: LIDAR_COLUMN_DIMENSIONS.BORDER,
        borderLeft: shouldMergeLeft ? "none" : LIDAR_COLUMN_DIMENSIONS.BORDER,
        borderRight: shouldMergeRight ? "none" : LIDAR_COLUMN_DIMENSIONS.BORDER,
        justifyContent: "space-around",
        alignItems: "center",
        background: backgroundColor
    };
};

/**
 * Bin component represents a single column in the Lidar tester grid
 * Supports visual merging with adjacent columns for flex configurations
 */
const Bin = (props) => {
    const {isAnomaly, sensorStates, columnLabel, shouldMergeRight, shouldMergeLeft, aisleLabel, device} = props;

    const getSensorProps = (index) => ({
        sensorState: sensorStates[index]
    });

    return (
        <div style={getBinColumnStyle(columnLabel, isAnomaly, shouldMergeRight, shouldMergeLeft, aisleLabel, device)}>
            {!isAnomaly && sensorStates && sensorStates.map((val, i) => (
                <LidarSensor key={i} {...getSensorProps(i)} />
            ))}
        </div>
    );
};

export default injectIntl(Bin);



// WEBPACK FOOTER //
// ./src/components/devicemanagement/lidar/lidarTester/Bin.js